/** Macro #30 — Social feeds (`#feed`): Following (auth) + Discover (public). */
import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { cn } from "../cn";
import { socialApi, creatorLabel, type FeedItem } from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { timeAgo } from "../activity-format";
import { useI18n } from "../../i18n";

type Tab = "following" | "discover";
type TFn = (path: string, vars?: Record<string, string | number>) => string;

function itemActor(it: FeedItem): { label: string; wallet?: string } {
  if (it.type === "PUBLICATION" && it.owner_wallet) return { label: creatorLabel(it.owner_handle, it.owner_wallet), wallet: it.owner_wallet };
  if (it.actor_wallet) return { label: creatorLabel(it.actor_handle, it.actor_wallet), wallet: it.actor_wallet };
  return { label: "THE GRID" };
}

function itemTitle(it: FeedItem, t: TFn): string {
  switch (it.type) {
    case "PUBLICATION": return t("social.feed.titlePublication");
    case "ARTWORK": return it.title ? t("social.feed.titleArtworkNamed", { title: it.title }) : t("social.feed.titleArtwork");
    case "TERRITORY_ACQUIRED": return it.parcel_id != null ? t("social.feed.titleAcquiredParcel", { id: it.parcel_id }) : t("social.feed.titleAcquired");
    default: return it.type;
  }
}

function FeedRow({ it }: { it: FeedItem }) {
  const { t } = useI18n();
  const actor = itemActor(it);
  return (
    <a
      href={it.deep_link?.startsWith("#") ? it.deep_link : `#${it.deep_link}`}
      data-testid="feed-item"
      data-feed-type={it.type}
      className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.07] bg-[rgba(12,16,22,0.5)] px-4 py-3 no-underline transition-colors hover:border-white/15 hover:bg-white/[0.04]"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {actor.wallet ? (
            <span
              role="link"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); window.location.hash = `u/${actor.wallet}`; }}
              className="font-mono text-[11px] font-semibold text-white/75 hover:text-neon-teal"
            >
              {actor.label}
            </span>
          ) : (
            <span className="font-mono text-[11px] font-semibold text-white/60">{actor.label}</span>
          )}
          <span className="font-mono text-[10px] text-white/35">{timeAgo(it.ts)}</span>
        </div>
        <div className="mt-0.5 truncate text-[13px] text-white/85">{itemTitle(it, t)}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {typeof it.like_count === "number" && (
          <span className="font-mono text-[10px] text-white/40">♥ {it.like_count}</span>
        )}
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-white/30">{it.type}</span>
      </div>
    </a>
  );
}

function FeedList({ tab }: { tab: Tab }) {
  const { t } = useI18n();
  const q = useInfiniteQuery({
    queryKey: ["social", "feed", tab],
    queryFn: ({ pageParam, signal }) =>
      tab === "following"
        ? socialApi.feedFollowing(pageParam as string | null, signal)
        : socialApi.feedDiscover(pageParam as string | null, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (q.isLoading) return <div data-testid={`feed-loading-${tab}`} className="p-6 text-[13px] text-white/45">{t("social.feed.loading")}</div>;
  if (q.isError) return <div data-testid={`feed-error-${tab}`} className="p-6 text-[13px] text-state-danger">{t("social.feed.unavailable")}</div>;
  if (items.length === 0) {
    return (
      <div data-testid={`feed-empty-${tab}`} className="rounded-xl border border-white/[0.07] p-6 text-[13px] text-white/50">
        {tab === "following"
          ? t("social.feed.emptyFollowing")
          : t("social.feed.emptyDiscover")}
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      {items.map((it, i) => <FeedRow key={`${it.type}-${it.identity_id ?? it.artwork_id ?? it.parcel_id ?? i}-${i}`} it={it} />)}
      {q.hasNextPage && (
        <button
          type="button"
          data-testid={`feed-load-more-${tab}`}
          disabled={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
          className="font-mono text-[11px] uppercase tracking-[0.12em] text-neon-teal/80 hover:text-neon-teal disabled:opacity-40"
        >
          {q.isFetchingNextPage ? t("social.feed.loadingMore") : t("social.feed.loadMore")}
        </button>
      )}
    </div>
  );
}

export function FeedsRoute() {
  const { t } = useI18n();
  const { isAuthenticated } = useSession();
  const [tab, setTab] = useState<Tab>(isAuthenticated ? "following" : "discover");

  const tabBtn = (tb: Tab, label: string) => (
    <button
      type="button"
      data-testid={`feed-tab-${tb}`}
      data-active={tab === tb ? "true" : "false"}
      onClick={() => setTab(tb)}
      className={cn(
        "relative pb-2 font-mono text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors",
        tab === tb ? "text-white" : "text-white/45 hover:text-white/75",
      )}
    >
      {label}
      <span className={cn("absolute inset-x-0 -bottom-px h-px", tab === tb ? "bg-neon-teal" : "bg-transparent")} />
    </button>
  );

  return (
    <div data-testid="feeds-root" className="mx-auto max-w-3xl px-5 py-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-[26px] font-semibold text-white">{t("social.feed.title")}</h1>
        <a href="#u/me" data-testid="feeds-my-profile" onClick={(e) => { e.preventDefault(); window.location.hash = "u/me"; }}
           className="font-mono text-[11px] uppercase tracking-[0.12em] text-white/55 hover:text-white no-underline">
          {t("social.feed.yourProfile")}
        </a>
      </div>
      <div className="mt-5 flex items-center gap-6 border-b border-white/10">
        {tabBtn("following", t("social.feed.following"))}
        {tabBtn("discover", t("social.feed.discover"))}
      </div>
      <div className="mt-5">
        {tab === "following" && !isAuthenticated ? (
          <a href="#auth" data-testid="feed-following-signin"
             className="inline-block rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[13px] text-white/60 no-underline hover:text-white">
            {t("social.feed.followingSignin")}
          </a>
        ) : (
          <FeedList tab={tab} />
        )}
      </div>
    </div>
  );
}
